import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { api } from '../../../lib/api';

type CampaignOut = {
  id: number;
  name: string;
  subject: string;
  audience: 'school_admins' | 'external_schools' | string;
  status: 'draft' | 'prepared' | 'sending' | 'sent' | string;
  created_at: string;
  prepared_at?: string | null;
  sent_at?: string | null;
  auto_send_enabled?: boolean;
  auto_send_daily_limit?: number;
  auto_send_hour?: number;
  auto_send_minute?: number;
  auto_send_timezone?: string;
  auto_send_next_run_at?: string | null;
  auto_send_last_run_at?: string | null;
  auto_send_last_error?: string | null;
};

type CampaignDetailOut = CampaignOut & {
  body_html: string;
};

type CampaignRecipientOut = {
  id: number;
  email: string;
  status: string;
  error?: string | null;
  sent_at?: string | null;
  school_name?: string | null;
  county?: string | null;
  city?: string | null;
  contact_name?: string | null;
};

type CampaignStatsOut = {
  recipients_total: number;
  queued: number;
  sent: number;
  failed: number;
  unsubscribed: number;
  opens: number;
};

type EmailUnsubscribeOut = {
  id: number;
  email: string;
  reason?: string | null;
  created_at?: string | null;
};

type EmailUnsubscribeListOut = {
  total: number;
  items: EmailUnsubscribeOut[];
};

type ExternalRecipientIn = {
  email: string;
  school_name?: string | null;
  county?: string | null;
  city?: string | null;
  contact_name?: string | null;
};

type PreviewOut = {
  from_name: string;
  from_email: string;
  html: string;
  text: string;
};

function normalizeHeader(h: string): string {
  return String(h || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

function detectDelimiter(headerLine: string): string {
  const s = String(headerLine || '');
  const comma = (s.match(/,/g) || []).length;
  const semi = (s.match(/;/g) || []).length;
  const tab = (s.match(/\t/g) || []).length;
  if (tab >= comma && tab >= semi && tab > 0) return '\t';
  if (semi >= comma && semi > 0) return ';';
  return ',';
}

function parseCsvLine(line: string, delimiter: string): string[] {
  const out: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        const next = line[i + 1];
        if (next === '"') {
          current += '"';
          i += 1;
          continue;
        }
        inQuotes = false;
        continue;
      }
      current += ch;
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      continue;
    }

    if (ch === delimiter) {
      out.push(current.trim());
      current = '';
      continue;
    }

    current += ch;
  }
  out.push(current.trim());
  return out;
}

function toCsv(rows: Array<Record<string, string>>): string {
  const headers = Object.keys(rows[0] || {});
  const esc = (v: string) => {
    const s = String(v ?? '');
    if (/[",\n;]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const out = [headers.join(',')];
  for (const r of rows) out.push(headers.map((h) => esc(r[h] ?? '')).join(','));
  return out.join('\n');
}

function downloadText(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function isValidEmail(email: string): boolean {
  const e = String(email || '').trim().toLowerCase();
  if (!e || e.length > 254) return false;
  if (e.includes(' ')) return false;
  if (!e.includes('@')) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

export default function EmailCampaignsPanel() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const [unsubsQ, setUnsubsQ] = useState('');
  const [unsubsItems, setUnsubsItems] = useState<EmailUnsubscribeOut[]>([]);
  const [unsubsTotal, setUnsubsTotal] = useState(0);
  const [unsubsOffset, setUnsubsOffset] = useState(0);
  const [unsubsLoading, setUnsubsLoading] = useState(false);
  const [unsubsError, setUnsubsError] = useState('');
  const [unsubsResubscribeId, setUnsubsResubscribeId] = useState<number | null>(null);
  const unsubsLimit = 50;

  const [campaigns, setCampaigns] = useState<CampaignOut[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const selected = useMemo(() => campaigns.find((c) => c.id === selectedId) || null, [campaigns, selectedId]);

  const [stats, setStats] = useState<CampaignStatsOut | null>(null);
  const [sendLimit, setSendLimit] = useState<number>(30);
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [autoDailyLimit, setAutoDailyLimit] = useState<number>(30);
  const [autoHour, setAutoHour] = useState<number>(9);
  const [autoMinute, setAutoMinute] = useState<number>(0);
  const [autoTimezone, setAutoTimezone] = useState<string>('Europe/Bucharest');

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('Campanie școli');
  const [newSubject, setNewSubject] = useState('Platforma Scola — propunere de prezentare (gratuit)');
  const [newAudience, setNewAudience] = useState<'external_schools' | 'school_admins'>('external_schools');
  const [newBody, setNewBody] = useState(
    [
      '<p>Bună ziua,</p>',
      '<p>Sunt {{contact_name}} de la <strong>Scola</strong> (platformă pentru management educațional).</p>',
      '<p>Am vrea să vă arătăm o prezentare scurtă (10–15 min) pentru {{school_name}}.</p>',
      '<p>Dacă vă interesează, răspundeți la acest email și vă trimitem detalii.</p>',
      '<p>Cu respect,<br/>Echipa Scola</p>',
    ].join('\n')
  );

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [previewFrom, setPreviewFrom] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState<'html' | 'text'>('html');

  const [sampleEmail, setSampleEmail] = useState('test@example.com');
  const [sampleSchoolName, setSampleSchoolName] = useState('Scoala Gimnaziala Nr. 1');
  const [sampleCounty, setSampleCounty] = useState('Arges');
  const [sampleCity, setSampleCity] = useState('Pitesti');
  const [sampleContactName, setSampleContactName] = useState('Echipa Scola');

  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const csvInputRef = useRef<HTMLInputElement | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editSubject, setEditSubject] = useState('');
  const [editAudience, setEditAudience] = useState<'external_schools' | 'school_admins'>('external_schools');
  const [editBody, setEditBody] = useState('');

  const [importFile, setImportFile] = useState<File | null>(null);
  const [parsedRecipients, setParsedRecipients] = useState<ExternalRecipientIn[]>([]);
  const [parsedInfo, setParsedInfo] = useState<string>('');

  const loadCampaigns = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await api.get<CampaignOut[]>('/api/marketing/platform/campaigns');
      const rows = resp.data || [];
      setCampaigns(rows);
      if (!selectedId && rows.length) setSelectedId(rows[0].id);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Eroare la încărcarea campaniilor.');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async (campaignId: number) => {
    try {
      const resp = await api.get<CampaignStatsOut>(`/api/marketing/platform/campaigns/${campaignId}/stats`);
      setStats(resp.data);
    } catch (e: any) {
      setStats(null);
      setError(e?.response?.data?.detail || 'Eroare la încărcarea statisticilor.');
    }
  };

  const loadUnsubscribes = async ({ reset }: { reset: boolean }) => {
    const nextOffset = reset ? 0 : unsubsOffset;

    setUnsubsLoading(true);
    setUnsubsError('');
    try {
      const resp = await api.get<EmailUnsubscribeListOut>('/api/marketing/platform/unsubscribes', {
        params: {
          q: unsubsQ?.trim() ? unsubsQ.trim() : undefined,
          limit: unsubsLimit,
          offset: nextOffset,
        },
      });

      const total = Number(resp?.data?.total ?? 0) || 0;
      const items = resp?.data?.items || [];

      setUnsubsTotal(total);
      setUnsubsItems((prev) => (reset ? items : [...prev, ...items]));
      setUnsubsOffset(nextOffset + items.length);
    } catch (e: any) {
      setUnsubsError(e?.response?.data?.detail || 'Eroare la \u00EEnc\u0103rcarea dezabon\u0103rilor.');
    } finally {
      setUnsubsLoading(false);
    }
  };

  const handleResubscribe = async (unsubscribeId: number) => {
    const row = unsubsItems.find((x) => x.id === unsubscribeId) || null;
    const email = String(row?.email || '').trim();

    const ok = window.confirm(`Reabonezi adresa: ${email || `#${unsubscribeId}`} ?`);
    if (!ok) return;

    setUnsubsResubscribeId(unsubscribeId);
    setUnsubsError('');
    try {
      await api.post(`/api/marketing/platform/unsubscribes/${unsubscribeId}/resubscribe`);
      setUnsubsItems((prev) => prev.filter((x) => x.id !== unsubscribeId));
      setUnsubsTotal((t) => Math.max(0, (Number(t) || 0) - 1));
    } catch (e: any) {
      setUnsubsError(e?.response?.data?.detail || 'Eroare la reabonare.');
    } finally {
      setUnsubsResubscribeId(null);
    }
  };

  useEffect(() => {
    void loadCampaigns();
    void loadUnsubscribes({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setStats(null);
      return;
    }
    void loadStats(selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  useEffect(() => {
    if (!selected) return;
    setAutoEnabled(Boolean(selected.auto_send_enabled));
    setAutoDailyLimit(Number(selected.auto_send_daily_limit ?? 30) || 30);
    setAutoHour(Number(selected.auto_send_hour ?? 9) || 9);
    setAutoMinute(Number(selected.auto_send_minute ?? 0) || 0);
    setAutoTimezone(String(selected.auto_send_timezone || 'Europe/Bucharest'));
  }, [selectedId, selected]);

  const handleCreate = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await api.post<CampaignOut>('/api/marketing/platform/campaigns', {
        name: newName,
        subject: newSubject,
        body_html: newBody,
        audience: newAudience,
      });
      const c = resp.data;
      setCreateOpen(false);
      await loadCampaigns();
      setSelectedId(c?.id ?? null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Eroare la crearea campaniei.');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    setError('');
    try {
      const resp = await api.post<PreviewOut>('/api/marketing/platform/campaigns/preview', {
        subject: newSubject,
        body_html: newBody,
        audience: newAudience,
        sample: {
          email: sampleEmail,
          school_name: sampleSchoolName,
          county: sampleCounty,
          city: sampleCity,
          contact_name: sampleContactName,
        },
      });
      setPreviewHtml(resp.data?.html || '');
      setPreviewText(resp.data?.text || '');
      setPreviewFrom(`${resp.data?.from_name || 'Scola'} <${resp.data?.from_email || ''}>`);
      setPreviewMode('html');
      setPreviewOpen(true);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Eroare la generarea preview-ului.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleUploadImage = async (file: File) => {
    setLoading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await api.post<{ url: string }>('/api/marketing/platform/email-assets/upload-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = String(resp.data?.url || '').trim();
      if (!url) throw new Error('missing_url');
      const snippet = `<p><img src="${url}" alt="" style="max-width:100%;height:auto;border-radius:12px;" /></p>`;
      setNewBody((prev) => `${prev}\n${snippet}\n`);
      setParsedInfo('Imagine încărcată și inserată în Body HTML.');
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Eroare la încărcarea imaginii.');
    } finally {
      setLoading(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const parseCsv = async (file: File) => {
    setParsedRecipients([]);
    setParsedInfo('');
    setError('');
    try {
      const text = await file.text();
      const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter((l) => l.trim().length > 0);
      if (!lines.length) {
        setParsedInfo('Fișier gol.');
        return;
      }
      const delimiter = detectDelimiter(lines[0]);
      const header = parseCsvLine(lines[0], delimiter).map(normalizeHeader);
      const idx = (keys: string[]) => {
        for (const k of keys) {
          const i = header.indexOf(normalizeHeader(k));
          if (i >= 0) return i;
        }
        return -1;
      };

      const emailIdx = idx(['email', 'e_mail', 'mail', 'contact_email']);
      const schoolIdx = idx(['school_name', 'scoala', 'unitate', 'unitate_scolara', 'name']);
      const countyIdx = idx(['county', 'judet', 'județ']);
      const cityIdx = idx(['city', 'localitate', 'oras', 'oraș']);
      const contactIdx = idx(['contact_name', 'persoana_contact', 'contact']);

      if (emailIdx < 0) {
        setParsedInfo('Nu găsesc coloana email. Pune un header: email, school_name, county, city, contact_name.');
        return;
      }

      const recipients: ExternalRecipientIn[] = [];
      let invalid = 0;
      for (const line of lines.slice(1)) {
        const cols = parseCsvLine(line, delimiter);
        const email = String(cols[emailIdx] || '').trim();
        if (!isValidEmail(email)) {
          invalid += 1;
          continue;
        }
        recipients.push({
          email,
          school_name: schoolIdx >= 0 ? String(cols[schoolIdx] || '').trim() || null : null,
          county: countyIdx >= 0 ? String(cols[countyIdx] || '').trim() || null : null,
          city: cityIdx >= 0 ? String(cols[cityIdx] || '').trim() || null : null,
          contact_name: contactIdx >= 0 ? String(cols[contactIdx] || '').trim() || null : null,
        });
      }

      setParsedRecipients(recipients);
      setParsedInfo(`OK: ${recipients.length} destinatari validați; ${invalid} linii invalide ignorate.`);
    } catch {
      setParsedInfo('Eroare la citirea/parcarea CSV.');
    }
  };

  const handleImport = async () => {
    if (!selected) {
      setError('Selectează o campanie.');
      return;
    }
    if ((selected.audience || '') !== 'external_schools') {
      setError("Importul este disponibil doar pentru audience='external_schools'.");
      return;
    }
    if (!parsedRecipients.length) {
      setError('Nu există destinatari de importat.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const resp = await api.post(`/api/marketing/platform/campaigns/${selected.id}/import-recipients`, {
        recipients: parsedRecipients,
      });
      const created = Number(resp?.data?.created ?? 0) || 0;
      const skipped = Number(resp?.data?.skipped ?? 0) || 0;
      const skippedInvalid = Number(resp?.data?.skipped_invalid ?? 0) || 0;
      const skippedUnsub = Number(resp?.data?.skipped_unsubscribed ?? 0) || 0;
      const skippedDup = Number(resp?.data?.skipped_duplicate ?? 0) || 0;
      const examples = Array.isArray(resp?.data?.skipped_examples) ? resp.data.skipped_examples : [];
      const exampleLines = examples
        .slice(0, 12)
        .map((x: any) => `- ${String(x?.email || '').trim() || '(gol)'} (${String(x?.reason || 'skipped')})`)
        .filter(Boolean);
      setParsedInfo(
        [
          `Import: created=${created}, skipped=${skipped}`,
          `skipped_invalid=${skippedInvalid}, skipped_unsubscribed=${skippedUnsub}, skipped_duplicate=${skippedDup}`,
          exampleLines.length ? `Exemple skip:\n${exampleLines.join('\n')}` : '',
        ]
          .filter(Boolean)
          .join('\n')
      );
      await loadStats(selected.id);
      await loadCampaigns();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Eroare la import destinatari.');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!selected) {
      setError('Selectează o campanie.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const resp = await api.post(`/api/marketing/platform/campaigns/${selected.id}/send?limit=${Math.max(1, Math.min(500, Number(sendLimit) || 30))}`);
      const info = `Send: sent=${resp?.data?.sent ?? 0}, failed=${resp?.data?.failed ?? 0}, unsubscribed=${resp?.data?.unsubscribed ?? 0}, remaining=${resp?.data?.remaining ?? 0}`;
      setParsedInfo(info);
      await loadStats(selected.id);
      await loadCampaigns();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Eroare la trimitere.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequeue = async (mode: 'all' | 'sent_failed') => {
    if (!selected) {
      setError('Selectează o campanie.');
      return;
    }
    const ok = window.confirm(
      mode === 'all'
        ? `Retrimiți campania #${selected.id} către toți destinatarii (în afară de dezabonați)?`
        : `Retrimiți campania #${selected.id} doar către destinatarii SENT/FAILED (în afară de dezabonați)?`
    );
    if (!ok) return;

    setLoading(true);
    setError('');
    try {
      const resp = await api.post(`/api/marketing/platform/campaigns/${selected.id}/requeue`, { mode });
      setParsedInfo(`Requeue: ${resp?.data?.requeued ?? 0}`);
      await loadStats(selected.id);
      await loadCampaigns();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Eroare la retrimitere.');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoSave = async () => {
    if (!selected) {
      setError('Selectează o campanie.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const resp = await api.post(`/api/marketing/platform/campaigns/${selected.id}/autosend`, {
        enabled: autoEnabled,
        daily_limit: Math.max(1, Math.min(500, Number(autoDailyLimit) || 30)),
        hour: Math.max(0, Math.min(23, Number(autoHour) || 9)),
        minute: Math.max(0, Math.min(59, Number(autoMinute) || 0)),
        timezone: autoTimezone || 'Europe/Bucharest',
      });
      const next = resp?.data?.next_run_at ? new Date(resp.data.next_run_at).toLocaleString('ro-RO') : '-';
      setParsedInfo(`Auto-send: enabled=${autoEnabled ? 'da' : 'nu'}, next=${next}`);
      await loadCampaigns();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Eroare la configurarea auto-send.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCampaign = async () => {
    if (!selected) {
      setError('Selectează o campanie.');
      return;
    }
    const ok = window.confirm(`Ștergi campania #${selected.id} (“${selected.name}”) și toți destinatarii ei?`);
    if (!ok) return;

    setLoading(true);
    setError('');
    try {
      await api.delete(`/api/marketing/platform/campaigns/${selected.id}`);
      setSelectedId(null);
      setStats(null);
      await loadCampaigns();
      setParsedInfo('Campanie ștearsă.');
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Eroare la ștergerea campaniei.');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = async () => {
    if (!selected) {
      setError('Selecteaz\u0103 o campanie.');
      return;
    }
    setEditLoading(true);
    setError('');
    try {
      const resp = await api.get<CampaignDetailOut>(`/api/marketing/platform/campaigns/${selected.id}`);
      const c = resp.data;
      setEditId(c.id);
      setEditName(c.name || '');
      setEditSubject(c.subject || '');
      setEditAudience(((c.audience as any) || 'external_schools') as any);
      setEditBody((c.body_html || '').trim());
      setEditOpen(true);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Eroare la \u00EEnc\u0103rcarea campaniei.');
    } finally {
      setEditLoading(false);
    }
  };

  const saveEdit = async () => {
    if (!editId) return;
    setEditLoading(true);
    setError('');
    try {
      await api.put(`/api/marketing/platform/campaigns/${editId}`, {
        name: editName,
        subject: editSubject,
        audience: editAudience,
        body_html: editBody,
      });
      setEditOpen(false);
      await loadCampaigns();
      setSelectedId(editId);
      await loadStats(editId);
      setParsedInfo('Campanie actualizat\u0103.');
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Eroare la salvarea campaniei.');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDownloadFailed = async () => {
    if (!selected) {
      setError('Selectează o campanie.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const resp = await api.get<CampaignRecipientOut[]>(
        `/api/marketing/platform/campaigns/${selected.id}/recipients?status=failed&limit=5000`
      );
      const rows = (resp.data || []).map((r) => ({
        email: r.email || '',
        school_name: String(r.school_name || ''),
        county: String(r.county || ''),
        city: String(r.city || ''),
        contact_name: String(r.contact_name || ''),
        error: String(r.error || ''),
        sent_at: String(r.sent_at || ''),
      }));
      if (!rows.length) {
        setParsedInfo('Nu există destinatari eșuați.');
        return;
      }
      downloadText(`campanie_${selected.id}_failed.csv`, toCsv(rows));
      setParsedInfo(`Export: ${rows.length} eșuate.`);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Eroare la export eșuate.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSample = () => {
    const sample = toCsv([
      {
        email: 'secretariat@scoala.ro',
        school_name: 'Școala Gimnazială Nr. 1',
        county: 'Argeș',
        city: 'Pitești',
        contact_name: 'Secretariat',
      },
    ]);
    downloadText('destinatari_scoli_sample.csv', sample);
  };

  const audienceOptions = [
    { value: 'external_schools', label: 'Școli externe (prospectare)' },
    { value: 'school_admins', label: 'Admini școli (în platformă)' },
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-3 sm:p-4">
      <div className="flex flex-col sm:flex-row sm:items-center items-start justify-between gap-3">
        <div>
          <div className="font-semibold text-foreground">Campanii email</div>
          <div className="text-xs text-muted-foreground">Creează, importă destinatari (CSV) și trimite în batch (limită/zi).</div>
        </div>
        <div className="flex flex-wrap items-center gap-2 justify-end w-full sm:w-auto">
          <Button variant="outline" size="sm" iconName="RefreshCw" onClick={() => void loadCampaigns()} disabled={loading}>
              Reîncarcă
          </Button>
          <Button variant="default" size="sm" iconName="Plus" onClick={() => setCreateOpen(true)} disabled={loading}>
            Campanie nouă
          </Button>
        </div>
      </div>

      {error ? <div className="mt-3 text-sm text-error">{error}</div> : null}
      {parsedInfo ? <div className="mt-3 text-xs text-muted-foreground whitespace-pre-line">{parsedInfo}</div> : null}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-1">
          <div className="text-sm font-medium text-foreground mb-2">Lista campanii</div>
          <div className="space-y-2 max-h-[38vh] sm:max-h-[520px] overflow-auto pr-1">
            {campaigns.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelectedId(c.id)}
                className={`w-full text-left border rounded-lg p-3 ${
                  selectedId === c.id ? 'border-primary bg-primary/5' : 'border-border bg-background'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-foreground truncate">{c.name}</div>
                  <div className="text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{c.status}</div>
                </div>
                <div className="text-xs text-muted-foreground truncate mt-1">{c.subject}</div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  #{c.id} • {c.audience}
                </div>
              </button>
            ))}
            {!campaigns.length && !loading ? (
              <div className="text-sm text-muted-foreground">Nu există campanii încă.</div>
            ) : null}
          </div>
        </div>

        <div className="lg:col-span-2">
          {selected ? (
            <div className="space-y-4">
              <div className="border border-border rounded-lg p-4 bg-background">
                <div className="flex flex-col sm:flex-row sm:items-start items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-base font-semibold text-foreground truncate">{selected.name}</div>
                    <div className="text-sm text-muted-foreground truncate">{selected.subject}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Audience: <span className="font-medium">{selected.audience}</span> • Status:{' '}
                      <span className="font-medium">{selected.status}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 justify-start sm:justify-end w-full sm:w-auto">
                    <Button variant="outline" size="sm" iconName="BarChart3" onClick={() => void loadStats(selected.id)}>
                      Stats
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Download"
                      onClick={() => void handleDownloadFailed()}
                      disabled={loading}
                    >
                      Eșuate CSV
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Edit"
                      onClick={() => void openEdit()}
                      disabled={loading || editLoading}
                    >
                      Editează
                    </Button>
                    <Button variant="outline" size="sm" iconName="Trash2" onClick={() => void handleDeleteCampaign()} disabled={loading}>
                      Șterge
                    </Button>
                  </div>
                </div>

                {stats ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3">
                    {[
                      { label: 'Total', v: stats.recipients_total },
                      { label: 'Queued', v: stats.queued },
                      { label: 'Sent', v: stats.sent },
                      { label: 'Failed', v: stats.failed },
                      { label: 'Unsub', v: stats.unsubscribed },
                      { label: 'Opens', v: stats.opens },
                    ].map((x) => (
                      <div key={x.label} className="border border-border rounded-md p-2">
                        <div className="text-[11px] text-muted-foreground">{x.label}</div>
                        <div className="text-lg font-bold text-foreground">{x.v}</div>
                      </div>
                    ))}
                  </div>
                ) : null}

                <div className="flex flex-wrap items-end gap-2 mt-4">
                  <Input
                    label="Limită batch"
                    type="number"
                    value={String(sendLimit)}
                    onChange={(e) => setSendLimit(Number(e.target.value))}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="RotateCw"
                    onClick={() => void handleRequeue('sent_failed')}
                    disabled={loading}
                    className="w-full sm:w-auto"
                  >
                    Retrimite (SENT+FAILED)
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="RotateCw"
                    onClick={() => void handleRequeue('all')}
                    disabled={loading}
                    className="w-full sm:w-auto"
                  >
                    Retrimite (toți)
                  </Button>
                  <Button
                    variant="default"
                    iconName="Send"
                    onClick={() => void handleSend()}
                    disabled={loading}
                    className="w-full sm:w-auto"
                  >
                    Trimite batch
                  </Button>
                  <div className="text-xs text-muted-foreground">Rulezi zilnic (ex. 30/zi) până se termină coada.</div>
                </div>
              </div>

              <div className="border border-border rounded-lg p-4 bg-background">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-sm font-medium text-foreground">Trimitere automată</div>
                    <div className="text-xs text-muted-foreground">
                      Trimite zilnic până se termină coada (backend trebuie pornit cu ENABLE_MARKETING_AUTOSEND=1).
                    </div>
                  </div>
                  <Button variant="outline" size="sm" iconName="Save" onClick={() => void handleAutoSave()} disabled={loading}>
                    Salvează
                  </Button>
                </div>

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-5 gap-2">
                  <label className="flex items-center gap-2 text-sm text-foreground">
                    <input type="checkbox" checked={autoEnabled} onChange={(e) => setAutoEnabled(e.target.checked)} />
                    Activ
                  </label>
                  <Input label="Limită/zi" type="number" value={String(autoDailyLimit)} onChange={(e) => setAutoDailyLimit(Number(e.target.value))} />
                  <Input label="Ora" type="number" value={String(autoHour)} onChange={(e) => setAutoHour(Number(e.target.value))} />
                  <Input label="Minut" type="number" value={String(autoMinute)} onChange={(e) => setAutoMinute(Number(e.target.value))} />
                  <Input label="TZ" value={String(autoTimezone)} onChange={(e) => setAutoTimezone(e.target.value)} />
                </div>

                <div className="mt-2 text-xs text-muted-foreground">
                  Next: {selected?.auto_send_next_run_at ? new Date(selected.auto_send_next_run_at).toLocaleString('ro-RO') : '-'}
                  {selected?.auto_send_last_error ? ` • Ultima eroare: ${selected.auto_send_last_error}` : ''}
                </div>
              </div>

              <div className="border border-border rounded-lg p-4 bg-background">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div>
                    <div className="text-sm font-medium text-foreground">Import destinatari (CSV)</div>
                    <div className="text-xs text-muted-foreground">Header recomandat: email, school_name, county, city, contact_name.</div>
                  </div>
                  <Button variant="outline" size="sm" iconName="Download" onClick={handleDownloadSample}>
                    CSV exemplu
                  </Button>
                </div>

                <input
                  ref={csvInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setImportFile(f);
                    if (f) void parseCsv(f);
                  }}
                />
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Upload"
                    onClick={() => {
                      if (csvInputRef.current) csvInputRef.current.value = '';
                      csvInputRef.current?.click();
                    }}
                    className="w-full sm:w-auto"
                    disabled={loading}
                  >
                    Alege CSV
                  </Button>
                </div>

                {importFile ? (
                  <div className="text-xs text-muted-foreground mt-2">
                    Fișier: <span className="font-medium">{importFile.name}</span>
                  </div>
                ) : null}

                {parsedRecipients.length ? (
                  <div className="mt-3">
                    <div className="text-xs text-muted-foreground mb-2">
                      Preview (primele 5): {parsedRecipients.slice(0, 5).map((r) => r.email).join(', ')}
                    </div>
                    <Button variant="default" iconName="Upload" onClick={() => void handleImport()} disabled={loading}>
                      Importă ({parsedRecipients.length})
                    </Button>
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">Selectează o campanie din stânga.</div>
          )}
        </div>
      </div>

      <div className="border border-border rounded-lg p-4 bg-background mt-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="text-sm font-medium text-foreground">{'Dezabona\u021Bi'}</div>
            <div className="text-xs text-muted-foreground">
              {'Lista adreselor dezabonate (global) + reabonare manual\u0103.'}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2 w-full sm:w-auto">
            <div className="w-full sm:w-[260px]">
              <Input
              label={'Caut\u0103 email'}
              placeholder="ex: @scola.ro"
              value={unsubsQ}
              onChange={(e) => setUnsubsQ(e.target.value)}
              onKeyDown={(e: any) => {
                if (e?.key === 'Enter') void loadUnsubscribes({ reset: true });
              }}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="RefreshCw"
              onClick={() => void loadUnsubscribes({ reset: true })}
              disabled={unsubsLoading}
              className="w-full sm:w-auto"
            >
              {'Re\u00EEnc\u0103rc\u0103'}
            </Button>
          </div>
        </div>

        {unsubsError ? <div className="mt-3 text-sm text-error">{unsubsError}</div> : null}

        <div className="mt-3 text-xs text-muted-foreground">Total: {unsubsTotal}</div>

        <div className="mt-3 space-y-2 sm:hidden">
          {unsubsItems.map((r) => {
            const dt = r.created_at ? new Date(r.created_at).toLocaleString('ro-RO') : '';
            return (
              <div key={r.id} className="border border-border rounded-md p-3 bg-background">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-foreground break-words">{r.email}</div>
                    <div className="text-xs text-muted-foreground mt-1">{dt || '-'}</div>
                    <div className="text-xs text-muted-foreground mt-1">Motiv: {r.reason || '-'}</div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="RotateCw"
                    onClick={() => void handleResubscribe(r.id)}
                    disabled={unsubsLoading || unsubsResubscribeId === r.id}
                    className="shrink-0"
                  >
                    {'Reaboneaz\u0103'}
                  </Button>
                </div>
              </div>
            );
          })}

          {!unsubsItems.length && !unsubsLoading ? (
            <div className="text-sm text-muted-foreground">{'Nu exist\u0103 dezabon\u0103ri pentru filtrul curent.'}</div>
          ) : null}
        </div>

        <div className="mt-3 overflow-auto border border-border rounded-md hidden sm:block">
          <table className="min-w-[720px] w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">Email</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">Motiv</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">Data</th>
                <th className="text-right px-3 py-2 font-medium text-muted-foreground">{'Ac\u021Biune'}</th>
              </tr>
            </thead>
            <tbody>
              {unsubsItems.map((r) => {
                const dt = r.created_at ? new Date(r.created_at).toLocaleString('ro-RO') : '';
                return (
                  <tr key={r.id} className="border-t border-border">
                    <td className="px-3 py-2 font-medium text-foreground whitespace-nowrap">{r.email}</td>
                    <td className="px-3 py-2 text-muted-foreground">{r.reason || '-'}</td>
                    <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{dt || '-'}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="RotateCw"
                        onClick={() => void handleResubscribe(r.id)}
                        disabled={unsubsLoading || unsubsResubscribeId === r.id}
                      >
                        {'Reaboneaz\u0103'}
                      </Button>
                    </td>
                  </tr>
                );
              })}

              {!unsubsItems.length && !unsubsLoading ? (
                <tr>
                  <td className="px-3 py-3 text-sm text-muted-foreground" colSpan={4}>
                    {'Nu exist\u0103 dezabon\u0103ri pentru filtrul curent.'}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="mt-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="text-xs text-muted-foreground">
            {'Afi\u0219ate:'} {unsubsItems.length} / {unsubsTotal}
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="ChevronDown"
            onClick={() => void loadUnsubscribes({ reset: false })}
            disabled={unsubsLoading || unsubsItems.length >= unsubsTotal}
          >
            {'\u00CEnc\u0103rc\u0103 mai mult'}
          </Button>
        </div>
      </div>

      {createOpen ? (
        <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl bg-card border border-border rounded-xl max-h-[90vh] flex flex-col">
            <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-border">
              <div>
                <div className="text-lg font-semibold text-foreground">Campanie nouă</div>
                <div className="text-sm text-muted-foreground">
                  Placeholders: <span className="font-mono text-xs">{`{{school_name}}`}</span>,{' '}
                  <span className="font-mono text-xs">{`{{county}}`}</span>,{' '}
                  <span className="font-mono text-xs">{`{{city}}`}</span>,{' '}
                  <span className="font-mono text-xs">{`{{contact_name}}`}</span>.
                </div>
              </div>
              <button
                className="w-9 h-9 rounded-md hover:bg-muted flex items-center justify-center"
                onClick={() => setCreateOpen(false)}
                aria-label="Închide"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="px-5 py-4 overflow-y-auto">
              <div className="grid grid-cols-1 gap-3">
              <Input label="Nume" value={newName} onChange={(e) => setNewName(e.target.value)} />
              <Input label="Subiect" value={newSubject} onChange={(e) => setNewSubject(e.target.value)} />
              <Select
                label="Audience"
                value={newAudience}
                options={audienceOptions}
                onChange={(v) => setNewAudience((v ? String(v) : 'external_schools') as any)}
              />
              <div className="border border-border rounded-md p-3 bg-muted/30">
                <div className="text-sm font-medium text-foreground mb-2">Preview (date exemplu)</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input label="Email" value={sampleEmail} onChange={(e) => setSampleEmail(e.target.value)} />
                  <Input label="Contact" value={sampleContactName} onChange={(e) => setSampleContactName(e.target.value)} />
                  <Input label="Scoala" value={sampleSchoolName} onChange={(e) => setSampleSchoolName(e.target.value)} />
                  <Input label="Judet" value={sampleCounty} onChange={(e) => setSampleCounty(e.target.value)} />
                  <Input label="Oras" value={sampleCity} onChange={(e) => setSampleCity(e.target.value)} />
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-foreground mb-1">Body HTML</div>
                <textarea
                  value={newBody}
                  onChange={(e) => setNewBody(e.target.value)}
                  className="w-full min-h-[180px] px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="<p>...</p>"
                />
                <div className="flex items-center gap-2 mt-2">
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void handleUploadImage(f);
                    }}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Image"
                    onClick={() => imageInputRef.current?.click()}
                    disabled={loading}
                  >
                    Incarca imagine
                  </Button>
                  <div className="text-xs text-muted-foreground">Se insereaza un &lt;img&gt; cu URL public (HTTPS).</div>
                </div>
              </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border">
              <Button variant="outline" onClick={() => setCreateOpen(false)}>
                Renunță
              </Button>
              <Button variant="outline" iconName="Eye" onClick={() => void handlePreview()} loading={previewLoading}>
                Preview
              </Button>
              <Button variant="default" iconName="Plus" onClick={() => void handleCreate()} loading={loading}>
                Creează
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {previewOpen ? (
        <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-4xl bg-card border border-border rounded-xl overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border">
              <div>
                <div className="text-sm font-semibold text-foreground">Preview email</div>
                <div className="text-xs text-muted-foreground">From: {previewFrom}</div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant={previewMode === 'html' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPreviewMode('html')}
                >
                  HTML
                </Button>
                <Button
                  variant={previewMode === 'text' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPreviewMode('text')}
                >
                  Text
                </Button>
                <button
                  className="w-9 h-9 rounded-md hover:bg-muted flex items-center justify-center"
                  onClick={() => setPreviewOpen(false)}
                  aria-label="Close preview"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>
            </div>

            <div className="p-4">
              {previewMode === 'html' ? (
                <iframe
                  title="Email preview"
                  className="w-full h-[70vh] border border-border rounded-md bg-white"
                  srcDoc={previewHtml || '<p>(gol)</p>'}
                />
              ) : (
                <pre className="w-full h-[70vh] overflow-auto border border-border rounded-md bg-background p-3 text-xs whitespace-pre-wrap">
                  {previewText || '(gol)'}
                </pre>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {editOpen ? (
        <div className="fixed inset-0 z-[1350] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl bg-card border border-border rounded-xl max-h-[90vh] flex flex-col">
            <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-border">
              <div>
                <div className="text-lg font-semibold text-foreground">Editează campanie</div>
                <div className="text-sm text-muted-foreground">
                  Placeholders: <span className="font-mono text-xs">{`{{school_name}}`}</span>,{' '}
                  <span className="font-mono text-xs">{`{{county}}`}</span>,{' '}
                  <span className="font-mono text-xs">{`{{city}}`}</span>,{' '}
                  <span className="font-mono text-xs">{`{{contact_name}}`}</span>.
                </div>
              </div>
              <button
                className="w-9 h-9 rounded-md hover:bg-muted flex items-center justify-center"
                onClick={() => setEditOpen(false)}
                aria-label="Închide"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="px-5 py-4 overflow-y-auto">
              <div className="grid grid-cols-1 gap-3">
                <Input label="Nume" value={editName} onChange={(e) => setEditName(e.target.value)} />
                <Input label="Subiect" value={editSubject} onChange={(e) => setEditSubject(e.target.value)} />
                <Select
                  label="Audience"
                  value={editAudience}
                  options={audienceOptions}
                  onChange={(v) => setEditAudience((v ? String(v) : 'external_schools') as any)}
                />
                <div>
                  <div className="text-sm font-medium text-foreground mb-1">Body HTML</div>
                  <textarea
                    value={editBody}
                    onChange={(e) => setEditBody(e.target.value)}
                    className="w-full min-h-[220px] px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="<p>...</p>"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border">
              <Button variant="outline" onClick={() => setEditOpen(false)}>
                Renunță
              </Button>
              <Button variant="default" iconName="Save" onClick={() => void saveEdit()} loading={editLoading}>
                Salvează
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
